import React, { useState, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faChevronRight,faUpload } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; //direkte Unterstützung für Fußnoten, Durchstreichungen, Tabellen, Aufgabenlisten und URLs
import rehypeKatex from 'rehype-katex'; //um Mathematik darzustellen
import remarkMath from 'remark-math'; //Mathematik in Markdown zu unterstützen
import rehypeRaw from 'rehype-raw'; // HTML in markdown
import rehypeStringify from 'rehype-stringify';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeFormat from 'rehype-format';
import remarkEmoji from 'remark-emoji'; //display short cut emojis
import remarkCollapse from 'remark-collapse'; //not directly typed, make collapsable sections
import { Config } from '../../config/Config';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { useTrackedRun } from '../../hooks/useTrackedRun';
import { stateIconDone, stateIconError, stateIconWaiting } from '../../types/ai/states';
import { useNavigateBack } from '../../hooks/useNavigationBack';

//TODO: API Analyse speech
interface SpeechToTextProps {
    'audioFile': any| null;
    'fileName': string | null;
    'userPrompt':string,
    'assistantResponse':string | null;
};

//TODO: image displaying
export const SpeechToText = () => {
    const { t } = useTranslation(['apps','toolsApps']);
    const navigateBack = useNavigateBack();
    const [inputs,setInputs] = useState<SpeechToTextProps>({
        audioFile:null,
        fileName: null,
        userPrompt: t('stt.demoPrompt'),
        assistantResponse:''
    });
    const [displayResult,setDisplayResult] = useState(false);
    const baseUrl = Config.json.AiServiceProxyUrl;
    const [state,setState]= useState<null|string>(null);
    const { startRun, completeRun, failRun, insufficientCreditsDialog } = useTrackedRun();

    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayResult(false);
        const file = event.target.files ? event.target.files[0]  : null;
        setState('waiting');
        if(file){
            let formData = new FormData();
            formData.append('file',file);
            console.log(formData.get("file"));
            setState(null);
            setInputs(prev => ({
                    ...prev,
                    audioFile: formData,
                    fileName: file.name
                }));
        }
        else{
            setState('error');
        }
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const hanldeSendImageAndPrompt = async() => {
        setInputs(prev => ({
                ...prev,
                ['assistantResponse']: ""
            }));
        setState('waiting');
        const prompt = inputs.userPrompt ? inputs.userPrompt.trim() : 'Analyse this image and return text'.trim();
        const file = inputs.audioFile.get("file");
        if (!(file instanceof File)){
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: t('missingAudio')
            }));
            setState(null);
            setDisplayResult(true);
            return;
        }
        const runHandle = await startRun('stt', { estimatedCost: 0 });
        if (!runHandle) {
            // Either insufficient balance (dialog shown by useTrackedRun) or another failure -
            // fail closed, don't call cProxy without a tracked run.
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: "❌ " + t('error.requestFailed')
            }));
            setState(null);
            setDisplayResult(true);
            return;
        }

        try {
            //TODO: STT-Feature ist derzeit kaputt (leerer path, siehe TASKS.md) - Aufruf schlaegt
            //immer fehl, Credit-Tracking ist aber bereits vorbereitet fuer den Fix.
            const requestBody = new FormData();
            requestBody.append("file", file, file.name);
            requestBody.append("prompt",prompt);
            requestBody.append("role","user");

            const path = '';
            const res = await fetchWithAuth(`${baseUrl}/${path}`, {
                method: "POST",
                body: requestBody
            });

            if(!res.ok){
                let err = await res.text();
                failRun(runHandle, `Speech-to-text request failed (HTTP ${res.status})`);
                setInputs(prev => ({
                    ...prev,
                    ['assistantResponse']: "❌ " + t('error.prefix') + err
                }));
                setState(null);
                setDisplayResult(true);
                return;
            }

            const data = await res.json();
            const usage = data.raw?.usage;
            completeRun(runHandle, {
                inputTokens: usage?.prompt_tokens ?? 0,
                outputTokens: usage?.completion_tokens ?? 0,
            });
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: data.reply || t('noResponse')
            }));
            setState(null);
            setDisplayResult(true);

        } catch(err){
            failRun(runHandle, `Speech-to-text request error: ${err}`);
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: "❌ " + t('error.connectionError')
            }));
            setState(null);
            setDisplayResult(true);
        }
    };

    useEffect(()=>{
        document.title = t('apps:registry.app.speechToText.name')
    },[]);

    return (
    <>
        {insufficientCreditsDialog}
        <div className="container-fluid p-4">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                    {state
                        ? state === 'send'
                            ? <p className='h4'>{t('state.sending')}</p>
                            : state === 'waiting'
                                ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                : state === 'error'
                                    ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                    : state === 'done'
                                        ? <p className='h4'>{stateIconDone} {t('state.done')}</p>
                                        : <></>
                        : <></>
                    }
                    { displayResult && (
                        <>
                            <div className="row mb-2">
                                <div className='col-auto p-3'>
                                    <div className='row'>
                                        <div className='col-auto h5'>
                                            {t('prompt')}
                                        </div>
                                        <div className='col'>
                                            {inputs.userPrompt}
                                        </div>
                                    </div>
                                </div>
                                <div className='col-1'></div>
                                <div className='col  p-3 '>
                                    { inputs.assistantResponse
                                        ?   <>
                                                <div className='row mb-1 h5'>{t('response')}</div>
                                                <div>
                                                    <ReactMarkdown 
                                                        remarkPlugins={[remarkMath,remarkEmoji,remarkGfm, remarkParse,remarkRehype,[remarkCollapse,{test:'heading'}]]} 
                                                        rehypePlugins={[rehypeKatex,rehypeFormat,rehypeRaw, rehypeStringify]}
                                                    >
                                                            {inputs.assistantResponse}
                                                    </ReactMarkdown>    
                                                </div>
                                            </>
                                        : <></>
                                    }
                                </div>
                            </div>
                        </>
                        )}
                </div>
                
                <div className='row mt-2 me-5'>
                    <div className='col-auto'>
                        <label htmlFor="fileuploadImage" className=' fw-bold pe-2'>
                            <FontAwesomeIcon icon={faUpload} className='me-2' />
                            {t('stt.uploadAudio')}
                        </label>
                        <input
                            id = 'fileuploadImage'
                            type= 'file'
                            accept="audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/aac,audio/amr,audio/3gpp,.mp3,.wav,.mp4,.m4a,.aac,.amr,.3gp"
                            onChange={event => uploadFile(event)}
                            hidden
                        />
                        {
                            inputs.fileName
                            ? <>{inputs.fileName}</>
                            : <></>
                        }
                    </div>
                    
                </div>
                 <div className="row mt-2">
                    <div className="col">
                        <textarea
                            id="chat-input-vision"
                            className="form-control border border-secondary border-1 bg-transparent"
                            name="userPrompt"
                            placeholder={t('imagePromptPlaceholder')}
                            onChange={handleInputChange}
                            style={{ resize: 'none' }}
                            value={inputs.userPrompt}
                        />
                    </div>
                    <div
                    className="col-auto d-flex align-items-center"
                    onClick={() => hanldeSendImageAndPrompt()}
                    >
                        <button
                            className="btn-chat bg-transparent border border-secondary rounded-circle text-secondary"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={t('send')}
                            disabled={state === 'waiting' || inputs.audioFile === null}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default SpeechToText;